/**
 * CompanyQuickForm 컴포넌트 - 세금계산서 발급 시 간이 회사 등록 폼
 * (company-list/CompanyForm.tsx에서 통합됨)
 *
 * CompanyForm과 달리 4개 필드만 사용하는 간소화된 폼
 * - name, rfc, address, email
 */

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import type { CompanyQuickFormData, FormErrors, CompanyListTranslations } from './types';

interface CompanyQuickFormProps {
  formData: CompanyQuickFormData;
  errors: FormErrors;
  t: CompanyListTranslations;
  onFormChange: (field: keyof CompanyQuickFormData, value: string) => void;
  onSave: () => void;
  onCancel: () => void;
}

export function CompanyQuickForm({
  formData,
  errors,
  t,
  onFormChange,
  onSave,
  onCancel
}: CompanyQuickFormProps) {
  return (
    <div className="p-6 space-y-4">
      <div>
        <Label htmlFor="companyName">{t.companyName}</Label>
        <Input
          id="companyName"
          value={formData.name}
          onChange={(e) => onFormChange('name', e.target.value)}
          placeholder={t.companyName}
          className={errors.name ? 'border-red-500' : ''}
        />
        {errors.name && (
          <p className="text-sm text-red-500 mt-1">{errors.name}</p>
        )}
      </div>

      <div>
        <Label htmlFor="rfc">{t.rfcNumber}</Label>
        <Input
          id="rfc"
          value={formData.rfc}
          onChange={(e) => onFormChange('rfc', e.target.value)}
          placeholder="ABC123456XYZ"
          className={errors.rfc ? 'border-red-500' : ''}
          maxLength={13}
        />
        {errors.rfc && (
          <p className="text-sm text-red-500 mt-1">{errors.rfc}</p>
        )}
      </div>

      <div>
        <Label htmlFor="address">{t.address}</Label>
        <textarea
          id="address"
          value={formData.address}
          onChange={(e) => onFormChange('address', e.target.value)}
          placeholder={t.address}
          className={`w-full p-3 border rounded-lg resize-none h-20 ${
            errors.address ? 'border-red-500' : 'border-border'
          }`}
        />
        {errors.address && (
          <p className="text-sm text-red-500 mt-1">{errors.address}</p>
        )}
      </div>

      <div>
        <Label htmlFor="email">{t.email}</Label>
        <Input
          id="email"
          type="email"
          value={formData.email}
          onChange={(e) => onFormChange('email', e.target.value)}
          placeholder="facturacion@empresa.com"
        />
      </div>

      <div className="flex gap-3 pt-4">
        <Button variant="outline" onClick={onCancel} className="flex-1">
          {t.cancel}
        </Button>
        <Button
          onClick={onSave}
          className="flex-1 bg-primary hover:bg-primary-hover text-white"
        >
          {t.save}
        </Button>
      </div>
    </div>
  );
}
